import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  apaAuthors,
  apaInTextAuthors,
  type CitationParts,
  chicagoBibliographyAuthors,
  chicagoNoteAuthors,
  chicagoShortAuthors,
  familyName,
  invertedName,
  mlaAuthors,
  mlaInTextAuthors,
  naturalName,
  type PartialDate,
  type PersonName,
  parsePartialDate,
  parsePersonName,
  tidyParts,
  withPeriod,
} from "@/lib/tool-kit/citation-parts";
import {
  capitalizeSubtitle,
  closer,
  get,
  italic,
  plain,
  titleCase,
} from "@/lib/tool-kit/legal-sources/common";
import {
  apaYearRange,
  beforeSubtitle,
  chicagoDateRange,
  chicagoShortTitle,
  type MediaLabels,
  type MediaValues,
  mlaDateRange,
  mlaRoleNames,
  mlaShortTitle,
  parsePeople,
} from "@/lib/tool-kit/media-sources/common";

/*
 * A museum exhibition — the event itself — and an exhibition catalog, in APA 7, MLA 9
 * and Chicago 18. A single artwork is a different source (the artwork citation
 * generator).
 *
 * Sources checked (October 2026):
 * - APA Style, "Artwork references" (§10.14), art exhibition: "Design for eternity:
 *   Architectural models from the ancient Americas [Exhibition]. (2015–2016). The Met
 *   Fifth Avenue, New York, NY, United States. https://www.metmuseum.org/exhibitions/
 *   listings/2015/design-for-eternity" and "Martinez, J.-L., & Douar, F. (2018–2019).
 *   Archaeology goes graphic [Exhibition]. The Louvre, Paris, France. …" — curators as
 *   authors (the title moves forward when none is credited), the year or range of
 *   years, the museum and its location, and the exhibition's web page; in text,
 *   "(Design for Eternity, 2015–2016)". A catalog is a book (§10.2).
 * - MLA 9: MLA Style Center, "How do I cite a museum exhibition?" — "Unbound: Narrative
 *   Art of the Plains. 12 Mar.-4 Dec. 2016, National Museum of the American Indian, New
 *   York." and, for a show of one artist's work, "Cave, Nick. Until. 15 Oct.
 *   2016-Aug. 2017, Mass MOCA, North Adams." A catalog is cited like a book.
 * - Chicago 18: the Chicago Style Q&A on citing an art exhibition — there is no
 *   standard format: italicize the exhibition's title, give enough detail to identify
 *   it (museum, city, dates), in the text or a note, and usually leave it out of the
 *   bibliography, where the catalog belongs instead. §14.134: exhibition catalogs are
 *   cited like books; "exh. cat." may be added in a note. The conjunction line in a
 *   catalog's bibliography entry follows the Manual's earlier example ("Published in
 *   conjunction with the exhibition … shown at …").
 */

export const EXHIBITION_FIELDS = {
  curators: "curators",
  artist: "artist",
  title: "title",
  museum: "museum",
  city: "city",
  region: "region",
  country: "country",
  startDate: "startDate",
  endDate: "endDate",
  url: "url",
  authors: "authors",
  authorRole: "authorRole",
  publisher: "publisher",
  year: "year",
  page: "page",
  exhibitionTitle: "exhibitionTitle",
  exhibitionVenue: "exhibitionVenue",
} as const;

const F = EXHIBITION_FIELDS;

/** The exhibition as an event, or the catalog published with it. */
export type ExhibitionSource = "exhibition" | "catalog";

export interface ExhibitionLabels extends MediaLabels {
  apaNote: string;
  mlaNote: string;
  chicagoNote: string;
  apaCatalogNote: string;
  mlaCatalogNote: string;
  chicagoCatalogNote: string;
}

const RE_LEADING_THE = /^the\s+/i;

function capitalizeFirst(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/* --------------------------------------------------------------- exhibition */

interface Exhibition {
  curators: PersonName[];
  artist: PersonName | null;
  title: string;
  museum: string;
  city: string;
  region: string;
  country: string;
  start: PartialDate | null;
  end: PartialDate | null;
  url: string;
}

function readExhibition(values: MediaValues): Exhibition {
  return {
    curators: parsePeople(get(values, F.curators)),
    artist: parsePersonName(get(values, F.artist)),
    title: capitalizeSubtitle(get(values, F.title)),
    museum: get(values, F.museum),
    city: get(values, F.city),
    region: get(values, F.region),
    country: get(values, F.country),
    start: parsePartialDate(get(values, F.startDate)),
    end: parsePartialDate(get(values, F.endDate)),
    url: get(values, F.url),
  };
}

function apaExhibitionReference(show: Exhibition): CitationParts {
  const date = `(${apaYearRange(show.start, show.end)}).`;
  const place = [show.museum, show.city, show.region, show.country]
    .filter(Boolean)
    .join(", ");
  const source = [place ? withPeriod(place) : "", show.url]
    .filter(Boolean)
    .join(" ");
  if (show.curators.length === 0) {
    return tidyParts([
      italic(show.title),
      plain(` [Exhibition]. ${date} ${source}`),
    ]);
  }
  const curators = apaAuthors(show.curators);
  return tidyParts([
    plain(`${curators}${closer(curators)} ${date} `),
    italic(show.title),
    plain(` [Exhibition]. ${source}`),
  ]);
}

function apaExhibition(
  show: Exhibition,
  labels: ExhibitionLabels
): CitationOutput {
  const years = apaYearRange(show.start, show.end);
  const short = beforeSubtitle(titleCase(show.title));
  const hasCurators = show.curators.length > 0;
  return {
    key: "apa",
    label: labels.apa,
    entries: [
      { label: labels.referenceList, parts: apaExhibitionReference(show) },
      {
        label: labels.parenthetical,
        parts: hasCurators
          ? [plain(`(${apaInTextAuthors(show.curators)}, ${years})`)]
          : [plain("("), italic(short), plain(`, ${years})`)],
      },
      {
        label: labels.narrative,
        parts: hasCurators
          ? [plain(`${apaInTextAuthors(show.curators, true)} (${years})`)]
          : [italic(short), plain(` (${years})`)],
      },
    ],
    note: labels.apaNote,
  };
}

function mlaExhibition(
  show: Exhibition,
  labels: ExhibitionLabels
): CitationOutput {
  const title = titleCase(show.title);
  const curators = show.curators.map(naturalName);
  const elements = [
    curators.length > 0 ? `curated by ${mlaRoleNames(curators)}` : "",
    mlaDateRange(show.start, show.end),
    show.museum,
    show.city,
  ].filter(Boolean);
  const parts: CitationParts = [];
  if (show.artist) {
    const artist = invertedName(show.artist);
    parts.push(plain(`${artist}${closer(artist)} `));
  }
  parts.push(italic(title), plain(closer(title)));
  if (elements.length > 0) {
    parts.push(plain(` ${withPeriod(capitalizeFirst(elements.join(", ")))}`));
  }
  const inText: CitationParts = show.artist
    ? [plain(`(${familyName(show.artist)})`)]
    : [plain("("), italic(mlaShortTitle(title)), plain(")")];
  return {
    key: "mla",
    label: labels.mla,
    entries: [
      { label: labels.worksCited, parts: tidyParts(parts) },
      { label: labels.inText, parts: inText },
    ],
    note: labels.mlaNote,
  };
}

function chicagoExhibition(
  show: Exhibition,
  labels: ExhibitionLabels
): CitationOutput {
  const title = titleCase(show.title);
  const curated =
    show.curators.length > 0
      ? `exhibition curated by ${mlaRoleNames(show.curators.map(naturalName))}`
      : "exhibition";
  const elements = [
    curated,
    show.museum,
    show.city,
    chicagoDateRange(show.start, show.end),
    show.url,
  ].filter(Boolean);
  const note: CitationParts = [];
  if (show.artist) {
    note.push(plain(`${naturalName(show.artist)}, `));
  }
  note.push(italic(title), plain(`, ${withPeriod(elements.join(", "))}`));
  const short: CitationParts = show.artist
    ? [
        plain(`${familyName(show.artist)}, `),
        italic(chicagoShortTitle(title)),
        plain("."),
      ]
    : [italic(chicagoShortTitle(title)), plain(".")];
  return {
    key: "chicago",
    label: labels.chicago,
    entries: [
      { label: labels.footnote, parts: tidyParts(note) },
      { label: labels.shortNote, parts: short },
    ],
    note: labels.chicagoNote,
  };
}

/* ------------------------------------------------------------------ catalog */

interface Catalog {
  authors: PersonName[];
  editors: boolean;
  title: string;
  publisher: string;
  year: string;
  page: string;
  url: string;
  exhibitionTitle: string;
  exhibitionVenue: string;
}

function readCatalog(values: MediaValues): Catalog {
  const year = parsePartialDate(get(values, F.year));
  return {
    authors: parsePeople(get(values, F.authors)),
    editors: get(values, F.authorRole) === "editors",
    title: capitalizeSubtitle(get(values, F.title)),
    publisher: get(values, F.publisher),
    year: year ? String(year.year) : "",
    page: get(values, F.page),
    url: get(values, F.url),
    exhibitionTitle: titleCase(
      capitalizeSubtitle(get(values, F.exhibitionTitle))
    ),
    exhibitionVenue: get(values, F.exhibitionVenue),
  };
}

/** Chicago and MLA drop an initial "The" from a publisher's name. */
function shortPublisher(name: string): string {
  return name.replace(RE_LEADING_THE, "");
}

function apaCatalogReference(book: Catalog): CitationParts {
  const date = `(${book.year || "n.d."}).`;
  const source = [
    book.publisher ? `${book.publisher}${closer(book.publisher)}` : "",
    book.url,
  ]
    .filter(Boolean)
    .join(" ");
  if (book.authors.length === 0) {
    return tidyParts([italic(book.title), plain(`. ${date} ${source}`)]);
  }
  const names = apaAuthors(book.authors);
  const role = book.editors
    ? ` (${book.authors.length > 1 ? "Eds." : "Ed."}).`
    : closer(names);
  return tidyParts([
    plain(`${names}${role} ${date} `),
    italic(book.title),
    plain(`. ${source}`),
  ]);
}

function apaCatalog(book: Catalog, labels: ExhibitionLabels): CitationOutput {
  const year = book.year || "n.d.";
  const short = beforeSubtitle(titleCase(book.title));
  const hasAuthors = book.authors.length > 0;
  return {
    key: "apa",
    label: labels.apa,
    entries: [
      { label: labels.referenceList, parts: apaCatalogReference(book) },
      {
        label: labels.parenthetical,
        parts: hasAuthors
          ? [plain(`(${apaInTextAuthors(book.authors)}, ${year})`)]
          : [plain("("), italic(short), plain(`, ${year})`)],
      },
      {
        label: labels.narrative,
        parts: hasAuthors
          ? [plain(`${apaInTextAuthors(book.authors, true)} (${year})`)]
          : [italic(short), plain(` (${year})`)],
      },
    ],
    note: labels.apaCatalogNote,
  };
}

function mlaCatalog(book: Catalog, labels: ExhibitionLabels): CitationOutput {
  const title = titleCase(book.title);
  const parts: CitationParts = [];
  if (book.authors.length > 0) {
    const names = mlaAuthors(book.authors);
    const role = book.editors
      ? `, ${book.authors.length > 1 ? "editors" : "editor"}.`
      : closer(names);
    parts.push(plain(`${names}${role} `));
  }
  parts.push(italic(title), plain(closer(title)));
  const facts = [shortPublisher(book.publisher), book.year].filter(Boolean);
  if (facts.length > 0) {
    parts.push(plain(` ${facts.join(", ")}.`));
  }
  const inText: CitationParts =
    book.authors.length > 0
      ? [plain(`(${mlaInTextAuthors(book.authors)})`)]
      : [plain("("), italic(mlaShortTitle(title)), plain(")")];
  return {
    key: "mla",
    label: labels.mla,
    entries: [
      { label: labels.worksCited, parts: tidyParts(parts) },
      { label: labels.inText, parts: inText },
    ],
    note: labels.mlaCatalogNote,
  };
}

function chicagoEditorWord(book: Catalog): string {
  return book.authors.length > 1 ? "eds." : "ed.";
}

function chicagoCatalogNote(book: Catalog, title: string): CitationParts {
  const parts: CitationParts = [];
  if (book.authors.length > 0) {
    const names = chicagoNoteAuthors(book.authors);
    const role = book.editors ? `, ${chicagoEditorWord(book)}` : "";
    parts.push(plain(`${names}${role}, `));
  }
  const facts = [shortPublisher(book.publisher), book.year || "n.d."]
    .filter(Boolean)
    .join(", ");
  const tail = [book.page, book.url].filter(Boolean);
  parts.push(
    italic(title),
    plain(
      `, exh. cat. (${facts})${tail.length > 0 ? `, ${tail.join(", ")}` : ""}.`
    )
  );
  return tidyParts(parts);
}

function chicagoCatalogBibliography(
  book: Catalog,
  title: string
): CitationParts {
  const parts: CitationParts = [];
  if (book.authors.length > 0) {
    const names = chicagoBibliographyAuthors(book.authors);
    const role = book.editors ? `, ${chicagoEditorWord(book)}` : "";
    parts.push(plain(`${withPeriod(`${names}${role}`)} `));
  }
  parts.push(italic(title), plain(closer(title)));
  const facts = [shortPublisher(book.publisher), book.year || "n.d."].filter(
    Boolean
  );
  parts.push(plain(` ${facts.join(", ")}.`));
  if (book.url) {
    parts.push(plain(` ${book.url}.`));
  }
  const line = conjunctionLine(book);
  if (line) {
    parts.push(plain(` ${line}`));
  }
  return tidyParts(parts);
}

/**
 * "Published in conjunction with the exhibition “…,” shown at …." — the line CMOS's
 * catalog example adds to the bibliography entry. Empty when nothing is known.
 */
function conjunctionLine(book: Catalog): string {
  const lead = "Published in conjunction with";
  const venue = book.exhibitionVenue;
  if (book.exhibitionTitle && venue) {
    return `${lead} the exhibition “${book.exhibitionTitle},” shown at ${withPeriod(venue)}`;
  }
  if (book.exhibitionTitle) {
    return `${lead} the exhibition “${withPeriod(book.exhibitionTitle)}”`;
  }
  return venue ? `${lead} an exhibition shown at ${withPeriod(venue)}` : "";
}

function chicagoCatalog(
  book: Catalog,
  labels: ExhibitionLabels
): CitationOutput {
  const title = titleCase(book.title);
  const short: CitationParts =
    book.authors.length > 0
      ? [
          plain(`${chicagoShortAuthors(book.authors)}, `),
          italic(chicagoShortTitle(title)),
          plain("."),
        ]
      : [italic(chicagoShortTitle(title)), plain(".")];
  return {
    key: "chicago",
    label: labels.chicago,
    entries: [
      { label: labels.footnote, parts: chicagoCatalogNote(book, title) },
      { label: labels.shortNote, parts: short },
      {
        label: labels.bibliography,
        parts: chicagoCatalogBibliography(book, title),
      },
    ],
    note: labels.chicagoCatalogNote,
  };
}

/* -------------------------------------------------------------------- entry */

export function exhibitionIsReady(values: MediaValues): boolean {
  return get(values, F.title) !== "";
}

export function formatExhibition(
  source: ExhibitionSource,
  values: MediaValues,
  labels: ExhibitionLabels
): CitationOutput[] {
  if (source === "catalog") {
    const book = readCatalog(values);
    return [
      apaCatalog(book, labels),
      mlaCatalog(book, labels),
      chicagoCatalog(book, labels),
    ];
  }
  const show = readExhibition(values);
  return [
    apaExhibition(show, labels),
    mlaExhibition(show, labels),
    chicagoExhibition(show, labels),
  ];
}
